import { MarketplaceOrder } from "../../../api/marketplace/MarketplaceOrdersApi";
import { Redemption } from "../../../api/redemption/Redemption";
import { UsimOrderItemData } from "../../../api/usim/orders/UsimOrderItem";

export type RevenueSource = "Manual Submission" | "Take App";
export type RevenueStatus = "Paid" | "Canceled" | "Refunded";

export type RevenueOrder = {
  kind?: "physical";
  orderId: string;
  orderDate: string;
  source: RevenueSource;
  amountCents: number;
  status: RevenueStatus;
  quarter: string;
};

type PhysicalSalesRecord = Pick<UsimOrderItemData,
  "OrderNumber" | "orderProcessedDate" | "status" | "Sales_Currency" | "Sales_NetAmountReceived"
>;

export type RevenueSummary = {
  totalCents: number;
  manualCents: number;
  takeAppCents: number;
  manualPercentage: number;
  takeAppPercentage: number;
};

function parseUtc(value: string): Date {
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
  return new Date(hasTimezone ? value : `${value}Z`);
}

export function quarterKey(value: string | Date): string {
  const date = typeof value === "string" ? parseUtc(value) : value;
  const utcPlusEight = new Date(date.getTime() + (8 * 60 * 60 * 1000));
  const quarter = Math.floor(utcPlusEight.getUTCMonth() / 3) + 1;
  return `Q${quarter} ${utcPlusEight.getUTCFullYear()}`;
}

export function formatRevenueOrderDate(value: string): string {
  const utcPlusEight = new Date(parseUtc(value).getTime() + (8 * 60 * 60 * 1000));
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(utcPlusEight);
}

export function buildRevenueOrders(
  redemptions: Redemption[],
  marketplaceOrders: MarketplaceOrder[],
  physicalOrders: PhysicalSalesRecord[] = [],
): RevenueOrder[] {
  const takeAppOrderIds = new Set(
    marketplaceOrders
      .filter(order => order.MarketplacePlatform === "TAKE")
      .map(order => order.OrderId),
  );
  const grouped = new Map<string, Redemption[]>();

  for (const redemption of redemptions) {
    const rows = grouped.get(redemption.OrderId) ?? [];
    rows.push(redemption);
    grouped.set(redemption.OrderId, rows);
  }

  const orders: RevenueOrder[] = [];
  for (const [orderId, rows] of grouped) {
    const sortedRows = rows.toSorted((left, right) => left.SimIdx - right.SimIdx);
    const sales = sortedRows[0];
    const source: RevenueSource | undefined = takeAppOrderIds.has(orderId)
      ? "Take App"
      : (rows.some(row => row.ManualSubmitDetails != null)
        ? "Manual Submission"
        : undefined);

    if (
      source === undefined
      || sales.Sales_Currency !== "PHP"
      || typeof sales.Sales_NetAmountReceived !== "number"
      || !Number.isFinite(sales.Sales_NetAmountReceived)
    ) {
      continue;
    }

    const refunded = rows.some(row => row.IsRefunded === true);
    const canceled = rows.every(row => row.IsCanceled === true);
    orders.push({
      orderId,
      orderDate: sales.CreatedAt,
      source,
      amountCents: Math.round(sales.Sales_NetAmountReceived * 100),
      status: refunded ? "Refunded" : (canceled ? "Canceled" : "Paid"),
      quarter: quarterKey(sales.CreatedAt),
    });
  }

  const physicalGroups = new Map<string, PhysicalSalesRecord[]>();
  for (const record of physicalOrders) {
    const rows = physicalGroups.get(record.OrderNumber) ?? [];
    rows.push(record);
    physicalGroups.set(record.OrderNumber, rows);
  }
  for (const [orderId, rows] of physicalGroups) {
    const sales = rows.find(row => (
      row.Sales_Currency === "PHP"
      && typeof row.Sales_NetAmountReceived === "number"
      && Number.isFinite(row.Sales_NetAmountReceived)
    ));
    if (!sales) continue;

    orders.push({
      kind: "physical",
      orderId,
      orderDate: sales.orderProcessedDate,
      source: "Manual Submission",
      amountCents: Math.round(sales.Sales_NetAmountReceived! * 100),
      status: rows.every(row => row.status === "30") ? "Canceled" : "Paid",
      quarter: quarterKey(sales.orderProcessedDate),
    });
  }

  return orders.toSorted((left, right) => (
    right.orderDate.localeCompare(left.orderDate)
    || left.orderId.localeCompare(right.orderId)
  ));
}

export function summarizeRevenue(orders: RevenueOrder[]): RevenueSummary {
  let manualCents = 0;
  let takeAppCents = 0;

  for (const order of orders) {
    if (order.status === "Refunded") continue;
    if (order.source === "Manual Submission") {
      manualCents += order.amountCents;
    } else {
      takeAppCents += order.amountCents;
    }
  }

  const totalCents = manualCents + takeAppCents;
  return {
    totalCents,
    manualCents,
    takeAppCents,
    manualPercentage: totalCents === 0 ? 0 : (manualCents / totalCents) * 100,
    takeAppPercentage: totalCents === 0 ? 0 : (takeAppCents / totalCents) * 100,
  };
}

export function quarterSort(left: string, right: string): number {
  const parse = (value: string) => {
    const [quarter, year] = value.split(" ");
    return (Number(year) * 4) + Number(quarter.slice(1));
  };
  return parse(right) - parse(left);
}
